import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { doc, getDoc } from 'firebase/firestore'
import { Bath, BedDouble, Car, LandPlot, X } from 'lucide-react'
import { StreetViewPanel } from '@/components/property/StreetViewPanel'
import { Button } from '@/components/ui/button'
import { Spinner } from '@/components/ui/spinner'
import { splitAddressDisplay } from '@/lib/address'
import { db } from '@/lib/firebase'
import { cn } from '@/lib/utils'
import { parseProperty, type Property } from '@/types/models'

function formatLandSize(landSize: number): string {
  if (!(landSize > 0)) return 'N/A'
  return `${Math.round(landSize)} m²`
}

function formatStat(value: number | null): string {
  return value == null ? '—' : String(value)
}

/**
 * Owner-facing property preview for the campaign letter landing.
 * Same sheet chrome as the map property profile — no buyer CTAs.
 * Portaled to `document.body` so the letter page’s overflow doesn’t clip it.
 */
export function LetterPropertyPreviewModal({
  open,
  propertyId,
  confirmed,
  onConfirmedChange,
  onClaim,
  onClose,
  claiming = false,
  error = null,
}: {
  open: boolean
  propertyId: string
  confirmed: boolean
  onConfirmedChange: (next: boolean) => void
  onClaim: () => void
  onClose: () => void
  claiming?: boolean
  error?: string | null
}) {
  const [property, setProperty] = useState<Property | null>(null)
  const [loading, setLoading] = useState(false)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [exiting, setExiting] = useState(false)
  const [mounted, setMounted] = useState(false)

  useEffect(() => {
    setMounted(true)
  }, [])

  useEffect(() => {
    if (!open || !propertyId) {
      setProperty(null)
      setLoadError(null)
      return
    }
    let cancelled = false
    setLoading(true)
    setLoadError(null)
    ;(async () => {
      try {
        const snap = await getDoc(doc(db, 'properties', propertyId))
        if (cancelled) return
        if (!snap.exists()) {
          setProperty(null)
          setLoadError('Property not found')
        } else {
          setProperty(parseProperty(snap.id, snap.data() as Record<string, unknown>))
        }
      } catch (e) {
        if (!cancelled) {
          setProperty(null)
          setLoadError(e instanceof Error ? e.message : 'Failed to load property')
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [open, propertyId])

  useEffect(() => {
    if (!open) {
      setExiting(false)
      return
    }
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') dismiss()
    }
    window.addEventListener('keydown', onKey)
    return () => {
      document.body.style.overflow = prev
      window.removeEventListener('keydown', onKey)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  function dismiss() {
    if (exiting) return
    setExiting(true)
    window.setTimeout(onClose, 220)
  }

  if (!mounted || (!open && !exiting)) return null

  const { street, locality } = property
    ? splitAddressDisplay(property.fullAddress)
    : { street: '', locality: '' }

  return createPortal(
    <div
      className={cn(
        'fixed inset-0 z-[70] flex items-end justify-center bg-navy/60 p-0 backdrop-blur-sm sm:items-center sm:p-4',
        exiting ? 'map-sheet-backdrop-exit' : 'map-sheet-backdrop-enter',
      )}
      role="dialog"
      aria-modal="true"
      aria-labelledby="letter-property-preview-title"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) dismiss()
      }}
    >
      <div
        className={cn(
          'relative flex max-h-[min(92dvh,800px)] w-full max-w-lg flex-col overflow-hidden rounded-t-3xl bg-white shadow-2xl sm:rounded-3xl',
          exiting ? 'property-profile-panel-exit' : 'property-profile-panel-enter',
        )}
      >
        {!loading && !loadError && property ? (
          <div className="relative z-20 shrink-0 border-b border-border/50 bg-white px-5 pb-3.5 pt-7">
            <div className="flex items-start gap-2">
              <div className="min-w-0 flex-1">
                <h2
                  id="letter-property-preview-title"
                  className="text-2xl font-bold tracking-tight text-navy"
                >
                  {street || 'Address unavailable'}
                </h2>
                {locality ? (
                  <p className="text-[15px] font-semibold text-muted">{locality}</p>
                ) : null}
              </div>
              <button
                type="button"
                onClick={dismiss}
                className="rounded-lg p-2 text-muted transition hover:bg-surface hover:text-charcoal"
                aria-label="Close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>
        ) : (
          <div className="absolute right-3 top-3 z-10">
            <button
              type="button"
              onClick={dismiss}
              className="rounded-lg p-2 text-muted transition hover:bg-surface hover:text-charcoal"
              aria-label="Close"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        )}

        <div className="min-h-0 flex-1 overflow-y-auto px-5 pb-4 pt-4">
          {loading ? (
            <div className="flex min-h-[40vh] items-center justify-center">
              <Spinner />
            </div>
          ) : loadError || !property ? (
            <div className="space-y-3 py-10 text-center">
              <p className="font-semibold text-navy">Property unavailable</p>
              <p className="text-sm text-muted">{loadError ?? 'We couldn’t load this property.'}</p>
              <Button variant="outline" onClick={dismiss}>
                Close
              </Button>
            </div>
          ) : (
            <div className="flex flex-col gap-5">
              <div className="rounded-2xl border border-border px-4 py-4">
                {property.propertyType ? (
                  <div className="mb-4">
                    <p className="text-xs font-medium uppercase tracking-wide text-muted">
                      Property type
                    </p>
                    <p className="mt-1 font-semibold text-navy">{property.propertyType}</p>
                  </div>
                ) : null}
                <dl className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                  <Stat icon={LandPlot} label="Land" value={formatLandSize(property.landSize)} />
                  <Stat icon={BedDouble} label="Beds" value={formatStat(property.beds)} />
                  <Stat icon={Bath} label="Baths" value={formatStat(property.baths)} />
                  <Stat icon={Car} label="Cars" value={formatStat(property.cars)} />
                </dl>
              </div>

              {property.location.latitude || property.location.longitude ? (
                <StreetViewPanel
                  latitude={property.location.latitude}
                  longitude={property.location.longitude}
                />
              ) : null}
            </div>
          )}
        </div>

        {property && !loading ? (
          <div className="shrink-0 space-y-3 border-t border-border bg-white/95 px-5 py-4 backdrop-blur">
            <label className="flex items-start gap-2.5 text-sm leading-snug">
              <input
                type="checkbox"
                className="mt-0.5"
                checked={confirmed}
                onChange={(e) => onConfirmedChange(e.target.checked)}
              />
              <span>I confirm I am the legal owner of this property.</span>
            </label>
            {error ? <p className="text-sm text-danger">{error}</p> : null}
            <Button
              type="button"
              variant="primary"
              size="lg"
              className="h-14 w-full text-base"
              disabled={!confirmed || claiming}
              onClick={onClaim}
            >
              {claiming ? 'Submitting…' : 'Claim ownership'}
            </Button>
          </div>
        ) : null}
      </div>
    </div>,
    document.body,
  )
}

function Stat({
  icon: Icon,
  label,
  value,
}: {
  icon: typeof BedDouble
  label: string
  value: string
}) {
  return (
    <div className="rounded-xl bg-surface px-3 py-3">
      <div className="flex items-center gap-1.5 text-xs font-medium text-muted">
        <Icon className="h-3.5 w-3.5" />
        {label}
      </div>
      <div className="mt-1 text-lg font-semibold text-navy">{value}</div>
    </div>
  )
}
